import React, { useState } from 'react';
import AuthService from '../services/auth';
import Loader from './Loader';

function Login({ onLogin }) {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    twoFactorCode: ''
  });
  const [requiresTwoFactorCode, setRequiresTwoFactorCode] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const resetTwoFactorPrompt = () => {
    setRequiresTwoFactorCode(false);
    setFormData((current) => ({ ...current, twoFactorCode: '' }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await AuthService.login(
        formData.username,
        formData.password,
        requiresTwoFactorCode ? formData.twoFactorCode : ''
      );

      if (response.requiresTwoFactorCode) {
        setRequiresTwoFactorCode(true);
        setFormData((current) => ({ ...current, twoFactorCode: '' }));
        return;
      }

      onLogin();
    } catch (loginError) {
      setError(loginError.response?.data?.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-gradient">
      <div className="card shadow-lg border-0" style={{width: '400px', borderRadius: '15px'}}>
        <div className="card-body p-5">
          <div className="text-center mb-4">
            <h2 className="fw-bold text-primary">
              <i className="fas fa-boxes me-2"></i>Saral Asset Manager
            </h2>
            <p className="text-muted">Sign in to your account</p>
          </div>
          
          {error && <div className={`alert ${requiresTwoFactorCode ? 'alert-warning' : 'alert-danger'}`}>{error}</div>}
          
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <input
                type="text"
                className="form-control form-control-lg"
                placeholder="Username"
                value={formData.username}
                onChange={(e) => setFormData({...formData, username: e.target.value})}
                required
                style={{borderRadius: '10px'}}
              />
            </div>
            <div className="mb-3">
              <input
                type="password"
                className="form-control form-control-lg"
                placeholder="Password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                required
                style={{borderRadius: '10px'}}
              />
            </div>
            <button 
              type="submit" 
              className={`btn btn-primary btn-lg w-100 mb-3 ${loading ? 'btn-loading' : ''}`}
              style={{borderRadius: '10px'}}
              disabled={loading}
            >
              {loading ? (
                <div className="inline-loader">
                  <Loader size="small" />
                  <span>Signing In...</span>
                </div>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
        </div>
      </div>

      {requiresTwoFactorCode && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(15, 23, 42, 0.72)', zIndex: 2000 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '18px', overflow: 'hidden' }}>
              <div className="modal-header border-0 pb-0">
                <div>
                  <h5 className="modal-title fw-bold mb-1">
                    <i className="fas fa-shield-alt me-2 text-primary"></i>Verify Two-Factor Authentication
                  </h5>
                  <p className="text-muted mb-0">Enter a code from your authenticator app or use a backup code.</p>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={resetTwoFactorPrompt}
                  disabled={loading}
                  aria-label="Close"
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body pt-4">
                  {error && <div className="alert alert-danger">{error}</div>}

                  <div className="alert alert-light border">
                    <div className="small text-muted mb-1">Signing in as</div>
                    <div className="fw-semibold">{formData.username}</div>
                  </div>

                  <label className="form-label fw-semibold">Verification Code</label>
                  <input
                    type="text"
                    className="form-control form-control-lg"
                    placeholder="Enter authenticator or backup code"
                    value={formData.twoFactorCode}
                    onChange={(e) => setFormData({ ...formData, twoFactorCode: e.target.value })}
                    required
                    autoFocus
                    maxLength={16}
                    style={{ borderRadius: '12px', letterSpacing: '0.12em' }}
                  />
                  <small className="text-muted d-block mt-2">
                    Use the current 6-digit code, or a backup code if your authenticator is unavailable.
                  </small>
                </div>
                <div className="modal-footer border-0 pt-0">
                  <button type="button" className="btn btn-outline-secondary" onClick={resetTwoFactorPrompt} disabled={loading}>
                    Back
                  </button>
                  <button
                    type="submit"
                    className={`btn btn-primary ${loading ? 'btn-loading' : ''}`}
                    disabled={loading}
                    style={{ minWidth: '150px', borderRadius: '10px' }}
                  >
                    {loading ? (
                      <div className="inline-loader">
                        <Loader size="small" />
                        <span>Verifying...</span>
                      </div>
                    ) : (
                      'Verify Code'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;
